import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Car, Hotel, LogOut, Loader2, CheckCircle2, UserCheck, Clock, Wifi, WifiOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
const STATUS_KEY = {
  scheduled: "s_scheduled",
  en_route_pickup: "s_en_route_pickup",
  arrived_pickup: "s_arrived_pickup",
  passenger_onboard: "s_passenger_onboard",
  completed: "s_completed",
  cancelled: "s_cancelled",
};

export default function DriverApp() {
  const { user, logout } = useAuth();
  const tr = useT();
  const statusLabel = (s) => (STATUS_KEY[s] ? tr(`driver.${STATUS_KEY[s]}`) : (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()));
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/transport/my-trips").then((r) => setTrips(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const on = () => setOnline(true), off = () => setOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  const act = async (trip, action, label) => {
    if (!navigator.onLine) return toast.error(tr("driver.offlineErr"));
    setBusy(trip.id + action);
    try {
      await api.post(`/transport/trips/${trip.id}/${action}`);
      toast.success(label);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || tr("driver.networkErr"));
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-navy text-white">
      <header className="flex items-center justify-between px-4 h-16 border-b border-white/10 sticky top-0 bg-navy z-10">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-gold text-navy flex items-center justify-center"><Car className="h-5 w-5" /></div>
          <div><p className="font-heading font-semibold leading-none">{tr("driver.myTrips")}</p>
            <p className="text-[11px] text-white/50 mt-0.5">{user?.full_name}</p></div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1 text-xs ${online ? "text-emerald-300" : "text-amber-300"}`} data-testid="driver-online-status">
            {online ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}{online ? tr("driver.online") : tr("driver.offline")}
          </span>
          <button onClick={logout} data-testid="driver-logout" className="text-white/60 hover:text-white"><LogOut className="h-5 w-5" /></button>
        </div>
      </header>

      <main className="p-4 space-y-4 max-w-xl mx-auto">
        {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
          : trips.length === 0 ? <div className="p-16 text-center text-white/50">{tr("driver.noTrips")}</div>
          : trips.map((t) => {
            const done = t.status === "completed", cancelled = t.status === "cancelled";
            const pickedUp = !!t.picked_up_at || t.status === "passenger_onboard" || done;
            return (
              <div key={t.id} data-testid={`driver-trip-${t.id}`} className="rounded-xl bg-white/5 border border-white/10 p-4">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-lg">{t.patient_name}</p>
                  <span className="text-[11px] uppercase tracking-wide bg-white/10 rounded px-2 py-0.5" data-testid={`trip-status-${t.id}`}>{statusLabel(t.status)}</span>
                </div>
                <p className="text-sm text-white/70 mt-1 flex items-center gap-2"><Hotel className="h-3.5 w-3.5 text-gold" /> {t.hotel_name || tr("driver.hotelNotSet")}</p>
                <p className="text-sm text-white/60 mt-1 flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> {fmt(t.scheduled_time)}</p>
                {t.picked_up_at && <p className="text-xs text-emerald-300 mt-1">{tr("driver.pickedUp")}: {fmt(t.picked_up_at)}</p>}
                {t.delivered_at && <p className="text-xs text-emerald-300">{tr("driver.delivered")}: {fmt(t.delivered_at)}</p>}

                {!done && !cancelled && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button size="sm" disabled={pickedUp || busy === t.id + "pickup"} onClick={() => act(t, "pickup", tr("driver.pickupRecorded"))}
                      data-testid={`pickup-${t.id}`} className="bg-gold hover:bg-gold/90 text-navy gap-1 disabled:opacity-40">
                      {busy === t.id + "pickup" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />} {tr("driver.patientPickedUp")}
                    </Button>
                    <Button size="sm" disabled={!pickedUp || busy === t.id + "deliver"} onClick={() => act(t, "deliver", tr("driver.deliveryRecorded"))}
                      data-testid={`deliver-${t.id}`} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1 disabled:opacity-40">
                      {busy === t.id + "deliver" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {tr("driver.patientDelivered")}
                    </Button>
                  </div>
                )}
                {done && <p className="mt-3 text-sm text-emerald-300 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> {tr("driver.tripCompleted")}</p>}
                {cancelled && <p className="mt-3 text-sm text-white/40">{tr("driver.tripCancelled")}</p>}
              </div>
            );
          })}
      </main>
    </div>
  );
}
